import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Backdrop, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import ActionModalHeader from './ActionModalHeader';
import ActionModalFooter from './ActionModalFooter';
import ActionModalBody from './ActionModalBody';
import { useStyles } from './style';

/**
 * @name SweeprActionModal - jsx component for reusable action modal
 * @param {object} root0  - destructured object
 * @param {string} root0.id - Unique of the modal
 * @param {boolean} root0.open - specifies when modal is open or close
 * @param {Function} root0.handleClose - function to be executed when modal needs to be closed
 * @param {Function} root0.handleBack -function to handle back using back arrow
 * @param {string} root0.desktopImageSrc - Desktop View URL of image to be displayed in modal
 * @param {string} root0.mobileImageSrc - Mobile View URL of image to be displayed in modal
 * @param {string} root0.title - title of modal.
 * @param {string} root0.description - description/subtitle of the modal
 * @param {string} root0.iconSrc -  URL of icon to be displayed in modal
 * @param {Array} root0.cta - array of object's which contain all cta details
 * @param {string} root0.disclaimer - disclaimer of the modal
 * @param {boolean} root0.disableCloseOnBackdropClick - modal won't close on click of modal backdrop
 * @param {boolean} root0.disableCloseOnEscapeKeyDown - modal won't close on pressing escape key
 * @param {Function} root0.load - Display Skeleton until data is loaded
 * @param {boolean} root0.disablecta - Enables or disables cta
 * @param {Function} root0.handleAction - Function to handle cta actions
 * @returns {JSX.Element} - jsx component for reusable action modal
 */
const SweeprActionModal = ({
  id,
  open,
  handleClose,
  handleBack,
  desktopImageSrc,
  mobileImageSrc,
  title,
  description,
  cta,
  disclaimer,
  iconSrc,
  disableCloseOnBackdropClick,
  disableCloseOnEscapeKeyDown,
  load,
  disablecta,
  handleAction,
}) => {
  const theme = useTheme();
  const style = useStyles(theme);
  const sm = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * @name handleDefaultClose - Function close the modal
   * @param {string} reason - Reason fro closing modal on backdrop click and escape key
   */
  const handleDefaultClose = reason => {
    if (
      (reason === 'backdropClick' && disableCloseOnBackdropClick === true) ||
      (reason === 'escapeKeyDown' && disableCloseOnEscapeKeyDown === true)
    ) {
      handleClose();
    }
  };

  return (
    <Modal
      id={id}
      open={open}
      onClose={(event, reason) => {
        handleDefaultClose(event, reason);
      }}
      sx={style.modalRoot}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 50,
        },
      }}
    >
      <Box sx={style.modalContainer}>
        <Box sx={style.iconButtonContainer}>
          <IconButton sx={style.iconButton} onClick={handleBack}>
            {sm ? (
              <ArrowBackIcon fontSize='large' sx={style.arrowIcon} />
            ) : (
              <CloseIcon fontSize='large' sx={style.arrowIcon} />
            )}
          </IconButton>
        </Box>
        <Box sx={iconSrc && style.imageAndText}>
          <ActionModalHeader
            desktopImageSrc={desktopImageSrc}
            mobileImageSrc={mobileImageSrc}
            iconSrc={iconSrc}
          />
        </Box>
        <Box sx={style.descriptionBox}>
          <ActionModalBody title={title} description={description} load={load} />
        </Box>
        <ActionModalFooter
          cta={cta}
          handleAction={handleAction}
          disablecta={disablecta}
          disclaimer={disclaimer}
          load={load}
        />
      </Box>
    </Modal>
  );
};

SweeprActionModal.displayName = 'SweeprActionModal';
export default SweeprActionModal;
